import React, { useEffect, useState } from 'react';

import { RiDownload2Line } from '@remixicon/react';

import { Button } from '@/design-system/button';
import { Checkbox } from '@/design-system/checkbox';
import { FormModal } from '@/design-system/form-modal';
import { SearchSelect } from '@/design-system/search-select';
import { toast } from '@/design-system/toaster';
import {
  DISPATCH_STATUS_LABELS,
  DispatchExportError,
  DispatchStatus,
  dispatchesAPI,
} from '@/services/api/dispatches';
import { searchAllPitstopOptions } from '@/services/api/pitstops';
import { useSearchableOptions } from '@/hooks/useSearchableOptions';

// Global CSV export dialog (`GET /v1/dispatch/export`, scheduled-dispatch TRD
// v2 §D7) — required date range on committed_at (max 62 days), optional status
// + hub scope, and a grouped column picker. The Identitas group is always
// included server-side, so its boxes render checked + disabled.

const STATUSES: DispatchStatus[] = [
  'PREVIEW',
  'COMMITTED',
  'PARTIAL',
  'FAILED',
  'CANCELLED',
];

interface ColumnDef {
  key: string;
  label: string;
  /** Always exported — the checkbox is shown but locked. */
  required?: boolean;
  /** Small note under the label, for columns that are often empty. */
  hint?: string;
  /** Unchecked by default; the rest start on. */
  offByDefault?: boolean;
}

interface ColumnGroup {
  label: string;
  always?: boolean;
  columns: ColumnDef[];
}

/** Mirrors the BE's canonical column model (fixed group order). */
const COLUMN_GROUPS: ColumnGroup[] = [
  {
    label: 'Identitas',
    always: true,
    columns: [
      { key: 'dispatch_code', label: 'Dispatch Code' },
      { key: 'dispatch_date', label: 'Dispatch Date (WIB)' },
      { key: 'waybill', label: 'Waybill' },
      { key: 'receiver_name', label: 'Receiver Name' },
      { key: 'destination_address', label: 'Destination Address' },
    ],
  },
  {
    label: 'Client & alokasi',
    columns: [
      { key: 'client_name', label: 'Client Name' },
      { key: 'hub_name', label: 'Hub Name' },
      { key: 'zone_code', label: 'Zone Code' },
      { key: 'zone_name', label: 'Zone Name' },
      { key: 'rider_code', label: 'Rider Code' },
      { key: 'rider_name', label: 'Rider Name' },
      { key: 'po_item', label: 'PO Item', offByDefault: true },
    ],
  },
  {
    label: 'Waktu',
    columns: [
      {
        key: 'dispatch_scheduled_at',
        label: 'Dispatch Scheduled At (WIB)',
        hint: 'hanya terisi untuk dispatch terjadwal',
        offByDefault: true,
      },
      { key: 'committed_at', label: 'Committed At (WIB)' },
      { key: 'updated_at', label: 'Updated At (WIB)' },
    ],
  },
  {
    label: 'Hasil',
    columns: [
      { key: 'item_status', label: 'Item Status', required: true },
      { key: 'pod_url', label: 'POD URL' },
      { key: 'failure_reason', label: 'Failure Reason' },
    ],
  },
];

/** Locked-on columns: whole always-groups plus individually required ones. */
const REQUIRED_KEYS = COLUMN_GROUPS.flatMap((g) =>
  g.columns.filter((c) => g.always || c.required).map((c) => c.key)
);
const ALWAYS_COUNT = REQUIRED_KEYS.length;
/** Toggleable columns, and the subset that starts checked. */
const OPTIONAL_KEYS = COLUMN_GROUPS.flatMap((g) =>
  g.always ? [] : g.columns.filter((c) => !c.required).map((c) => c.key)
);
const DEFAULT_ON_KEYS = COLUMN_GROUPS.flatMap((g) =>
  g.always
    ? []
    : g.columns
        .filter((c) => !c.required && !c.offByDefault)
        .map((c) => c.key)
);

const MAX_RANGE_DAYS = 62;

const pad = (n: number) => String(n).padStart(2, '0');

const localDate = (d: Date): string =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Default: the last 7 days, ending today. */
const defaultFrom = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return localDate(d);
};
const defaultTo = (): string => localDate(new Date());

/** Inclusive day span of a `YYYY-MM-DD` pair; null when either is invalid. */
const rangeDays = (from: string, to: string): number | null => {
  const f = new Date(`${from}T00:00:00`);
  const t = new Date(`${to}T00:00:00`);
  if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime())) return null;
  return Math.round((t.getTime() - f.getTime()) / 86_400_000) + 1;
};

interface ExportDispatchesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ExportDispatchesModal: React.FC<ExportDispatchesModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [statuses, setStatuses] = useState<DispatchStatus[]>([]);
  const [hubId, setHubId] = useState('');
  const [selected, setSelected] = useState<string[]>(DEFAULT_ON_KEYS);
  const [downloading, setDownloading] = useState(false);

  // Server-side (`?search=`) hub/pitstop scope options.
  const hubSearch = useSearchableOptions(isOpen, searchAllPitstopOptions);

  // Reset the form every time the dialog opens.
  useEffect(() => {
    if (!isOpen) return;
    setFrom(defaultFrom());
    setTo(defaultTo());
    setStatuses([]);
    setHubId('');
    setSelected(DEFAULT_ON_KEYS);
    setDownloading(false);
  }, [isOpen]);

  const toggleStatus = (status: DispatchStatus) => {
    setStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const toggleColumn = (key: string) => {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const days = from && to ? rangeDays(from, to) : null;
  const rangeInvalid = days === null || days < 1;
  const rangeTooWide = days !== null && days > MAX_RANGE_DAYS;
  const canDownload =
    Boolean(from) && Boolean(to) && !rangeInvalid && !rangeTooWide && !downloading;

  const selectedCount = ALWAYS_COUNT + selected.length;

  const handleDownload = async () => {
    if (!canDownload) return;
    setDownloading(true);
    try {
      await dispatchesAPI.exportAll({
        from,
        to,
        status: statuses.length > 0 ? statuses : null,
        hubId: hubId || null,
        // Every optional column picked = same as the BE default — omit the
        // param so the server keeps its canonical "all columns" behaviour.
        columns:
          selected.length === OPTIONAL_KEYS.length
            ? null
            : OPTIONAL_KEYS.filter((k) => selected.includes(k)),
      });
      toast.success('Export dispatch dimulai', {
        description: `dispatches-${from}-${to}.csv · ${selectedCount} kolom`,
      });
      onClose();
    } catch (err) {
      const code = err instanceof DispatchExportError ? err.code : 'UNKNOWN';
      toast.error(
        code === 'EXPORT_RANGE_TOO_WIDE'
          ? `Rentang tanggal terlalu lebar (maks ${MAX_RANGE_DAYS} hari)`
          : 'Export dispatch gagal',
        { description: err instanceof Error ? err.message : 'Unknown error' }
      );
    } finally {
      setDownloading(false);
    }
  };

  const labelCls =
    'mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600';
  const dateCls =
    'h-9 rounded-lg border border-stroke-soft-200 bg-white px-3 font-mono text-sm tabular-nums text-text-strong-950 outline-none transition-colors focus:border-accent disabled:opacity-50';

  return (
    <FormModal
      open={isOpen}
      onClose={() => !downloading && onClose()}
      icon={<RiDownload2Line />}
      iconTone="primary"
      title="Export dispatch"
      subtitle="CSV global — satu baris per item, lintas dispatch dalam rentang tanggal commit."
      size="lg"
      closeOnBackdropClick={!downloading}
      closeOnEscape={!downloading}
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          <p className="text-[11px] text-text-sub-600 tabular-nums">
            {selectedCount} kolom terpilih
          </p>
          <div className="flex items-center gap-2">
            <Button
              tone="neutral"
              style="stroke"
              onClick={onClose}
              disabled={downloading}
            >
              Batal
            </Button>
            <Button onClick={() => void handleDownload()} disabled={!canDownload}>
              {downloading ? (
                <>
                  <i className="ri-loader-4-line mr-1.5 animate-spin text-[14px]" />
                  Menyiapkan…
                </>
              ) : (
                <>
                  <i className="ri-download-2-line mr-1.5 text-[14px]" />
                  Download CSV
                </>
              )}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label htmlFor="export-from" className={labelCls}>
              Dari (committed) <span className="text-error-base">*</span>
            </label>
            <input
              id="export-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              disabled={downloading}
              className={dateCls}
            />
          </div>
          <div>
            <label htmlFor="export-to" className={labelCls}>
              Sampai <span className="text-error-base">*</span>
            </label>
            <input
              id="export-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              disabled={downloading}
              className={dateCls}
            />
          </div>
          <div className="min-w-[220px] flex-1">
            <label className={labelCls}>Hub / pitstop (opsional)</label>
            <SearchSelect
              options={hubSearch.options.map((h) => ({
                value: h.id,
                label: `${h.code} · ${h.name}`,
              }))}
              value={hubId || undefined}
              selectedLabelFallback={hubId ? hubSearch.labelFor(hubId) : undefined}
              onChange={(v) => setHubId((v as string | undefined) ?? '')}
              onSearchChange={hubSearch.setSearch}
              disableLocalFilter
              loading={hubSearch.loading}
              placeholder="Semua hub"
              allowClear
              disabled={downloading}
              emptyMessage={hubSearch.error ?? 'No matching hub'}
            />
          </div>
        </div>
        {rangeTooWide && (
          <p className="text-[11px] text-error-base">
            <i className="ri-error-warning-line mr-1" aria-hidden="true" />
            Rentang {days} hari — maksimal {MAX_RANGE_DAYS} hari.
          </p>
        )}
        {!rangeTooWide && rangeInvalid && from && to && (
          <p className="text-[11px] text-error-base">
            <i className="ri-error-warning-line mr-1" aria-hidden="true" />
            Tanggal akhir harus sesudah (atau sama dengan) tanggal awal.
          </p>
        )}

        <div>
          <label className={labelCls}>
            Status dispatch{' '}
            <span className="font-normal normal-case tracking-normal">
              (kosong = semua)
            </span>
          </label>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {STATUSES.map((status) => (
              <label
                key={status}
                className="inline-flex cursor-pointer items-center gap-2 text-[13px] text-text-strong-950"
              >
                <Checkbox
                  size="sm"
                  checked={statuses.includes(status)}
                  onCheckedChange={() => toggleStatus(status)}
                  disabled={downloading}
                />
                {DISPATCH_STATUS_LABELS[status]}
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className={labelCls}>
            Kolom yang diekspor{' '}
            <span className="font-normal normal-case tracking-normal text-text-sub-600">
              · {selectedCount} kolom
            </span>
          </label>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {COLUMN_GROUPS.map((group) => (
              <div
                key={group.label}
                className="rounded-lg border border-stroke-soft-200 bg-bg-weak-50 p-3"
              >
                <p className="mb-2 flex items-baseline justify-between gap-2 text-[11px] font-semibold text-text-strong-950">
                  {group.label}
                  {group.always && (
                    <span className="shrink-0 text-[10px] font-normal text-text-sub-600">
                      kunci rekonsiliasi · selalu diekspor
                    </span>
                  )}
                </p>
                <div className="space-y-1.5">
                  {group.columns.map((col) => {
                    const locked = group.always || Boolean(col.required);
                    return (
                      <label
                        key={col.key}
                        className={`flex items-start gap-2 text-[12px] ${
                          locked
                            ? 'text-text-sub-600'
                            : 'cursor-pointer text-text-strong-950'
                        }`}
                      >
                        <Checkbox
                          size="sm"
                          className="mt-0.5"
                          checked={locked || selected.includes(col.key)}
                          disabled={locked || downloading}
                          onCheckedChange={() =>
                            !locked && toggleColumn(col.key)
                          }
                        />
                        <span>
                          {col.label}
                          {col.required && !group.always && (
                            <span className="ml-1 rounded bg-bg-soft-200 px-1 text-[9px] font-semibold uppercase text-text-sub-600">
                              wajib
                            </span>
                          )}
                          {col.hint && (
                            <span className="block text-[10px] leading-tight text-text-soft-400">
                              {col.hint}
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3 flex items-start gap-2 rounded-lg border border-stroke-soft-200 bg-bg-weak-50 px-3 py-2 text-[11px] leading-relaxed text-text-sub-600">
            <i className="ri-information-line mt-px text-[13px]" />
            <span>
              Format <b>CSV (UTF-8)</b>, satu baris per penerima. Resi &amp;
              status kiriman mengikuti sumber pengiriman hub; Dash melengkapi
              rute, zona, rider, dan jadwal. Kolom yang tidak berlaku untuk
              suatu client — misalnya jam dispatch pada dispatch manual —
              tampil kosong.
            </span>
          </div>
        </div>
      </div>
    </FormModal>
  );
};

export default ExportDispatchesModal;
